import React from "react";

interface ExamType {
  name: string;
  // Add other properties if needed
}

interface SubjectsTablesProps {
  registrationData: {
    exam_registration?: {
      subject_categories?: Record<string, unknown>;
    } | null;
  } | null;
  selectedExamTypes: ExamType[];
  isALSelected: boolean;
}

const SubjectsTables: React.FC<SubjectsTablesProps> = ({
  registrationData,
  selectedExamTypes,
  isALSelected,
}) => {
  const shouldRenderTables =
    selectedExamTypes.some((et) => et.name.includes("GCE O/L")) ||
    selectedExamTypes.some((et) => et.name.includes("GCE A/L")) ||
    selectedExamTypes.some((et) => et.name.includes("TVE I/L")) ||
    selectedExamTypes.some((et) => et.name.includes("TVE A/L"));

  if (!shouldRenderTables) {
    return null;
  }

  return (
    <>
      {/* Main Subjects Table For GCE O/L & GCE A/L */}
      {registrationData &&
        (() => {
          const subjectCategories =
            registrationData?.exam_registration?.subject_categories;
          if (!subjectCategories) {
            return (
              <div className="text-center py-8 text-gray-500">
                No subject data available for this school.
              </div>
            );
          }

          const hasGCEOL = selectedExamTypes.some((examType) =>
            examType.name.includes("GCE O/L")
          );
          const hasGCEAL = selectedExamTypes.some((examType) =>
            examType.name.includes("GCE A/L")
          );
          const hasTVEIL = selectedExamTypes.some((examType) =>
            examType.name.includes("TVE I/L")
          );
          const hasTVEAL = selectedExamTypes.some((examType) =>
            examType.name.includes("TVE A/L")
          );

          // If TVE I/L is selected (even without GCE O/L), use GCE O/L COMMON subjects
          const commonSubjects =
            hasGCEOL || hasTVEIL
              ? ((subjectCategories as Record<string, unknown>)[
                "GCEO/LCOMMON"
              ] as {
                code: string;
                name: string;
                student_count: string | number;
              }[]) || []
              : hasGCEAL || hasTVEAL
                ? ((subjectCategories as Record<string, unknown>)[
                  "GCEA/LCOMMON"
                ] as {
                  code: string;
                  name: string;
                  student_count: string | number;
                }[]) || []
                : [];

          return (
            <>
              {/* Common Subjects Table */}
              {commonSubjects.length > 0 && (
                <table className="gceo-subjects-table">
                  <tbody>
                    <tr>
                      <th colSpan={2} style={{ whiteSpace: "nowrap" }}>
                        COMMON SUBJECTS
                      </th>
                      <td
                        colSpan={2}
                        style={{ textAlign: "center", fontSize: "small" }}
                      >
                        {isALSelected
                          ? "Add the candidates for ALGCE and TVE A/L and write in the white column"
                          : "Add the candidates for OLGCE and TVE I/L and write in the white column"}
                      </td>
                      <td
                        rowSpan={commonSubjects.length + 1}
                        colSpan={2}
                        style={{ padding: 0 }}
                      >
                        <table>
                          <tbody>
                            <tr>
                              <td
                                colSpan={2}
                                style={{
                                  padding: "30px 0px 30px 0px",
                                  textAlign: "center",
                                }}
                              >
                                When you indicate the number for a Specialty,
                                also go to <strong>5005</strong> –{" "}
                                <strong>4520</strong> and indicate the number
                                for each general subject if applicable
                              </td>
                            </tr>
                            <tr>
                              <th colSpan={2} style={{ textAlign: "center" }}>
                                COMMERCIAL
                              </th>
                            </tr>
                            <tr>
                              <th style={{ textAlign: "center" }}>SPECIALTY</th>
                              <th style={{ textAlign: "center" }}>
                                # of Candidates
                              </th>
                            </tr>
                            {(
                              (subjectCategories as Record<string, unknown>)[
                              isALSelected
                                ? "TVEA/LCOMMERCIAL"
                                : "TVEI/LCOMMERCIAL"
                              ] as {
                                code: string;
                                name: string;
                                student_count: string | number;
                              }[] || []
                            ).map((subject) => (
                              <tr key={subject.code}>
                                <td>
                                  {subject.code} {subject.name}
                                </td>
                                <td>
                                  <span
                                    style={{
                                      textAlign: "center",
                                      display: "block",
                                    }}
                                  >
                                    {subject.student_count}
                                  </span>
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                    {commonSubjects.map((subject) => (
                      <tr key={subject.code}>
                        <td colSpan={2} style={{ whiteSpace: "nowrap" }}>
                          <span>
                            {subject.code} {subject.name}
                          </span>
                        </td>
                        <td className="shadedCell" colSpan={2}>
                          <span
                            style={{
                              textAlign: "center",
                              display: "block",
                            }}
                          >
                            {subject.student_count}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              )}

              {/* GCE O/L ONLY, TVE I/L ONLY, and Industrial Specialties Table */}
              <table className="gceo-subjects-table">
                <tbody>
                  <tr>
                    <th style={{ textAlign: "center" }} colSpan={2}>
                      {isALSelected ? "GCE A/L ONLY" : "GCE O/L ONLY"}
                    </th>
                    <th style={{ textAlign: "center" }} colSpan={2}>
                      {isALSelected ? "TVE A/L ONLY" : "TVE I/L ONLY"}
                    </th>
                    <th style={{ textAlign: "center" }} colSpan={2}>
                      INDUSTRIAL
                    </th>
                  </tr>
                  <tr>
                    <td colSpan={2} style={{ padding: 0 }}>
                      <table
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <tbody>
                          <tr>
                            <th style={{ textAlign: "center" }}>
                              # of Candidates
                            </th>
                            <th style={{ textAlign: "center" }}>SUBJECTS</th>
                          </tr>
                          {(() => {
                            const maxRows = isALSelected ? 18 : 16;
                            const subjects = (
                              ((subjectCategories as Record<string, unknown>)[
                                isALSelected ? "GCEA/LONLY" : "GCEO/LONLY"
                              ] as {
                                code: string;
                                name: string;
                                student_count: string | number;
                              }[]) || []
                            ).slice(0, maxRows);
                            const rows = subjects.map((subject) => (
                              <tr key={subject.code}>
                                <td>
                                  <span
                                    style={{
                                      textAlign: "center",
                                      display: "block",
                                    }}
                                  >
                                    {subject.student_count}
                                  </span>
                                </td>
                                <td>
                                  {subject.code} {subject.name}
                                </td>
                              </tr>
                            ));
                            // Add empty rows to pad to maxRows
                            for (let i = subjects.length; i < maxRows; i++) {
                              rows.push(
                                <tr
                                  key={`empty-left-${i}`}
                                  className="bg-gray-600"
                                >
                                  <td className="shadedCell"></td>
                                  <td className="shadedCell">.</td>
                                </tr>
                              );
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </td>
                    <td colSpan={2} style={{ padding: 0 }}>
                      <table
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <tbody>
                          <tr>
                            <th style={{ textAlign: "center" }}>SUBJECTS</th>
                            <th style={{ textAlign: "center" }}>
                              # of Candidates
                            </th>
                          </tr>
                          {(() => {
                            const maxRows = isALSelected ? 18 : 16;
                            const subjects = (
                              ((subjectCategories as Record<string, unknown>)[
                                isALSelected ? "TVEA/LONLY" : "TVEI/LONLY"
                              ] as {
                                code: string;
                                name: string;
                                student_count: string | number;
                              }[]) || []
                            ).slice(0, maxRows);
                            const rows = subjects.map((subject) => (
                              <tr key={subject.code}>
                                <td>
                                  {subject.code} {subject.name}
                                </td>
                                <td>
                                  <span
                                    style={{
                                      textAlign: "center",
                                      display: "block",
                                    }}
                                  >
                                    {subject.student_count}
                                  </span>
                                </td>
                              </tr>
                            ));
                            // Add empty rows to pad to maxRows
                            for (let i = subjects.length; i < maxRows; i++) {
                              rows.push(
                                <tr
                                  key={`empty-right-${i}`}
                                  className="bg-gray-600"
                                >
                                  <td className="shadedCell">.</td>
                                  <td className="shadedCell"></td>
                                </tr>
                              );
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </td>
                    <td colSpan={2} style={{ padding: 0 }}>
                      <table
                        style={{
                          width: "100%",
                          height: "100%",
                        }}
                      >
                        <tbody>
                          <tr>
                            <th style={{ textAlign: "center" }}>SPECIALTY</th>
                            <th style={{ textAlign: "center" }}>
                              # of Candidates
                            </th>
                          </tr>
                          {(() => {
                            const maxRows = isALSelected ? 18 : 16;
                            const subjects = (
                              ((subjectCategories as Record<string, unknown>)[
                                isALSelected
                                  ? "TVEA/LINDUSTRIAL"
                                  : "TVEI/LINDUSTRIAL"
                              ] as {
                                code: string;
                                name: string;
                                student_count: string | number;
                              }[]) || []
                            ).slice(0, maxRows);
                            const rows = subjects.map((subject) => (
                              <tr key={subject.code}>
                                <td>
                                  {subject.code} {subject.name}
                                </td>
                                <td>
                                  <span
                                    style={{
                                      textAlign: "center",
                                      display: "block",
                                    }}
                                  >
                                    {subject.student_count}
                                  </span>
                                </td>
                              </tr>
                            ));
                            // Add empty rows to pad to maxRows
                            for (let i = subjects.length; i < maxRows; i++) {
                              rows.push(
                                <tr
                                  key={`empty-industrial-${i}`}
                                  className="bg-gray-600"
                                >
                                  <td className="shadedCell">.</td>
                                  <td className="shadedCell"></td>
                                </tr>
                              );
                            }
                            return rows;
                          })()}
                        </tbody>
                      </table>
                    </td>
                  </tr>
                </tbody>
              </table>
            </>
          );
        })()}
    </>
  );
};

export default SubjectsTables;
